import React, { useEffect, useCallback, useState } from 'react'
import * as R from 'ramda'
import styled from 'styled-components'
import { Label, Segment } from 'semantic-ui-react'

import type { FeedbackWidgetPreviewState } from '../container'

import { ButtonGroup } from 'components/ButtonGroup'
import './styles.css'

const WIDGET_PREVIEW_CONTAINER_ID = 'widget-preview-container'

export default function WidgetPreview({ feedbackWidgetPreviewState }: WidgetPreviewProp) {
  const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop')
  const loadScript = useCallback((src) => {
    return new Promise<HTMLScriptElement>(function (resolve, reject) {
      const script = document.createElement('script')
      script.src = src
      // This ID is hard-coded in the widget.
      script.id = 'simplesat-widget-script'
      script.addEventListener('load', function () {
        resolve(script)
      })
      script.addEventListener('error', function (error) {
        reject(error)
      })
      document.body.appendChild(script)
    })
  }, [])

  useEffect(() => {
    const scriptPromise = loadScript(process.env.REACT_APP_WIDGET_SCRIPT_URL)

    // When the script is already loaded e.g. When mounted and is waiting for the preview API.
    postMessage(feedbackWidgetPreviewState)

    function onSvelteMount() {
      postMessage(feedbackWidgetPreviewState)
    }

    let script
    scriptPromise
      .then(function (loadedScript) {
        script = loadedScript
        getWidgetIframe()?.addEventListener('svelte-mount', onSvelteMount)
      })
      .catch(function (error) {
        throw error
      })

    return () => {
      if (script) {
        document.body.removeChild(script)
      }
      getWidgetIframe()?.removeEventListener('svelte-mount', onSvelteMount)
    }
  }, [feedbackWidgetPreviewState, loadScript, previewMode])

  function onPreviewModeChange(e) {
    setPreviewMode(e.target.value)
  }

  const shouldShowEmptyWidgetMessage =
    feedbackWidgetPreviewState?.type !== 'score' &&
    feedbackWidgetPreviewState?.answers?.length === 0

  if (previewMode === 'desktop') {
    return (
      <React.Fragment>
        <section className="flex justify-between items-center">
          <h3 className="m-0">Widget preview</h3>
          <ButtonGroup>
            <ButtonGroup.Button
              active={previewMode === 'desktop'}
              value="desktop"
              onClick={onPreviewModeChange}
            >
              <i className="far fa-desktop icon" />
              Desktop
            </ButtonGroup.Button>
            <ButtonGroup.Button active={false} value="mobile" onClick={onPreviewModeChange}>
              <i className="far fa-mobile-button icon" />
              Mobile
            </ButtonGroup.Button>
          </ButtonGroup>
        </section>

        <Segment.Group>
          <Segment compact className="text-left ss-widget-preview-browser-bar">
            <Label basic empty circular className="white" />
            <Label basic empty circular className="white" />
            <Label basic empty circular className="white" />
          </Segment>
          <div className="w-full relative">
            {shouldShowEmptyWidgetMessage ? (
              <EmptyWidgetMessage />
            ) : (
              <Wrapper>
                <a
                  id={WIDGET_PREVIEW_CONTAINER_ID}
                  href="https://simplesat.io"
                  data-source="preview"
                />
              </Wrapper>
            )}
          </div>
        </Segment.Group>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <section className="flex justify-between items-center">
        <h3 className="m-0">Widget preview</h3>
        <ButtonGroup>
          <ButtonGroup.Button active={false} value="desktop" onClick={onPreviewModeChange}>
            <i className="far fa-desktop icon" />
            Desktop
          </ButtonGroup.Button>
          <ButtonGroup.Button
            active={previewMode === 'mobile'}
            value="mobile"
            onClick={onPreviewModeChange}
          >
            <i className="far fa-mobile-button icon" />
            Mobile
          </ButtonGroup.Button>
        </ButtonGroup>
      </section>
      <Mobile className="mt-10" widgetType={R.prop('type', feedbackWidgetPreviewState)}>
        <div className="body">
          <div className="front">
            <div className="screen">
              <div className="screen-front">
                <div className="screen-front-speaker" />
                <div className="screen-front-camera" />
              </div>
              <div className="screen-view">
                {shouldShowEmptyWidgetMessage && <EmptyWidgetMessage />}
                <a
                  id={WIDGET_PREVIEW_CONTAINER_ID}
                  href="https://simplesat.io"
                  data-source="preview"
                />
              </div>
            </div>
            <div className="front-line" />
            <div className="front-line front-line-second" />
          </div>
          <div className="phoneButtons phoneButtons-right" />
          <div className="phoneButtons phoneButtons-left" />
          <div className="phoneButtons phoneButtons-left2" />
          <div className="phoneButtons phoneButtons-left3" />
          <div className="back" />
        </div>
      </Mobile>
    </React.Fragment>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 0;

  #widget-preview-container {
    flex-basis: 100%;
  }

  .scale-frame {
    width: calc(100% * 2);
    height: 800px;
    border: 0px;
    transform: scale(0.5);
    transform-origin: 0 0;
  }
`
const Mobile = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: row-reverse;
  height: 700px;

  .body {
    width: 380px;
    height: 700px;
    display: flex;
    transform: rotate(0deg);
    z-index: 10;
  }

  .body .back {
    display: flex;
    flex: 1;
    margin: 0.2em;
    border-radius: 3em;
    position: absolute;
    z-index: 1;
    width: calc(100% - 0.6em);
    height: calc(100% - 0.6em);
    top: 0;
    right: 0;
    box-shadow: 0 0 10em rgba(70, 70, 70, 0.1);
    transition: all 0.4s;
  }

  .body .front {
    display: flex;
    flex: 1;
    background-color: #292c2d;
    border-radius: 3em;
    margin: 0.2em;
    overflow: hidden;
    position: relative;
    box-shadow: 0 0.1em 0.4em rgba(255, 255, 255, 0.1) inset;
    z-index: 10;
  }
  .body .screen {
    display: flex;
    flex: 1;
    background-color: #191b1c;
    margin: 0.4em;
    border-radius: 2.6em;
    border: solid 0.2em #121415;
    position: relative;
    z-index: 10;
  }
  .body .front-line {
    background: linear-gradient(to right, #515455, #454748, #515455);
    position: absolute;
    z-index: 8;
    width: 100%;
    height: 0.5em;
    top: 10%;
  }
  .body .front-line-second {
    top: 90%;
  }
  .body .screen .screen-view {
    margin: 0.6em;
    border-radius: 2em;
    overflow: scroll;
    position: relative;
    width: 100%;
    position: relative;
    padding-top: 30px;
    background-color: white;
    -ms-overflow-style: none;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }

    display: flex;
    justify-content: center;
    ${(props) => props.widgetType === 'score' && `align-items: center;`}
  }
  .body .screen .screen-view .dribbbleLogo {
    width: 8em;
    height: 8em;
    border: solid 0.7em #d33572;
    border-radius: 50%;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
    background: #ea4c89;
    position: absolute;
    z-index: 10;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all ease 0.7s;
  }
  .body .screen .screen-view .dribbbleLogo.white {
    width: 0.9em;
    height: 0.9em;
    border: solid 0.07em #fff;
    position: relative;
    margin-left: 0.3em;
    background: transparent;
    box-sizing: border-box;
  }
  .body .screen:hover .screen-view .dribbbleLogo:not(.white) {
    transform: translateY(100%) translateZ(1px);
    transition: all ease 1s 0.1s;
  }
  .body .screen .screen-view .dribbbleLogo:after,
  .body .screen .screen-view .dribbbleLogo:before,
  .body .screen .screen-view .dribbbleLogo span {
    content: '';
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: solid 0.45em #d33572;
    position: absolute;
  }
  .body .screen .screen-view .dribbbleLogo.white:after,
  .body .screen .screen-view .dribbbleLogo.white:before,
  .body .screen .screen-view .dribbbleLogo.white span {
    border: solid 0.04em #fff;
  }
  .body .screen .screen-view .dribbbleLogo:after {
    bottom: 0;
    transform: translateY(63%) scaleY(1.65) scaleX(1.5) rotate(-30deg);
    transform-origin: top center;
  }
  .body .screen .screen-view .dribbbleLogo:before {
    top: 0;
    transform: translateY(-62%) scaleY(1.65) scaleX(1.5) rotate(-30deg);
    transform-origin: bottom center;
  }
  .body .screen .screen-view .dribbbleLogo span {
    top: 0;
    transform: translateY(-50%) scaleY(1.85) scaleX(1.5) rotate(-63deg);
    transform-origin: bottom right;
  }
  .body .screen .screen-view .hello {
    font-size: 2.5em;
    color: #fff;
    position: absolute;
    z-index: 60;
    opacity: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transition: all ease 0.3s;
    transform: translateY(40%);
    font-weight: 600;
  }
  .body .screen:hover .screen-view .hello {
    opacity: 1;
    transform: translateY(0%);
    transition: all ease 0.6s 0.7s;
  }
  .body .screen .screen-front {
    position: absolute;
    width: 100%;
    background-color: #191b1c;
    height: 1.8em;
    border-radius: 0 0 0.9em 0.9em;
    top: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 0.3em;
    box-sizing: border-box;
    margin-top: 0.5em;
    z-index: 999;
    border-top-left-radius: 2em;
    border-top-right-radius: 2em;
  }
  .body .screen .screen-front-speaker {
    background: #070808;
    border-radius: 0.2em;
    height: 0.35em;
    width: 28%;
  }
  .body .screen .screen-front-camera {
    height: 0.35em;
    width: 0.35em;
    background: #272727;
    margin-left: 0.5em;
    border-radius: 50%;
    margin-right: -0.8em;
  }
  .body .phoneButtons {
    width: 1em;
    height: 6em;
    position: absolute;
    z-index: 2;
    background: linear-gradient(to bottom, #212324, #2b2e31, #212324);
    box-shadow: 0 0 0.4em rgba(255, 255, 255, 0.1) inset;
    border-radius: 2px;
  }
  .body .phoneButtons-right {
    right: 0;
    top: 30%;
  }
  .body .phoneButtons-left {
    left: 0;
    top: 15%;
    height: 3em;
  }
  .body .phoneButtons-left2 {
    left: 0;
    top: 25%;
    height: 5em;
  }
  .body .phoneButtons-left3 {
    left: 0;
    top: calc(25% + 6em);
    height: 5em;
  }

  @media (max-height: 40em) {
    body {
      font-size: 70%;
    }
    .body {
      width: 48vh;
      height: 94vh;
    }
    .title .title__svg svg text {
      font-size: 4em;
    }
  }
`

type PreviewMode = 'desktop' | 'mobile'

type WidgetPreviewProp = {
  feedbackWidgetPreviewState: FeedbackWidgetPreviewState
}

function postMessage(feedbackWidgetPreviewState: FeedbackWidgetPreviewState) {
  if (feedbackWidgetPreviewState) {
    const message = JSON.stringify(feedbackWidgetPreviewState)
    getWidgetIframe()?.contentWindow?.postMessage(
      {
        type: 'set_content',
        content: message,
      },
      window.location.origin
    )
  }
}

function getWidgetIframe() {
  return document.getElementById(WIDGET_PREVIEW_CONTAINER_ID)?.getElementsByTagName('iframe')?.[0]
}

function EmptyWidgetMessage() {
  return (
    <div className="flex justify-center items-center ss-widget-preview-loading">
      🧐 No feedback matches the current filters.
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/Admin/FeedbackWidgetSettings/WidgetPreview/index.tsx